import React, { useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  TableActionTypes,
  useDataTable,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { AppealSubjectListingItem } from '../../../../../api/candidate/appeal/model';

const useStyles = makeStyles<{ isAllSubjectNotEligible: boolean }>()((theme, {isAllSubjectNotEligible}) =>
({
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
  dataTableSelectAllRowsCheckBox: {
    pointerEvents: (isAllSubjectNotEligible ? 'none' : 'auto'),
  },
}));

enum AppealEligibility {
  ELIGIBLE = 'ELIGIBLE',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  SUBMITTED = 'SUBMITTED',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
}

const tableColumns = (
  examData: AppealSubjectListingItem[],
  t: TFunction<'translation'>,
  classes: Record<string,string>
): MUIDataTableColumnDef[] => [
    {
      name: 'subject',
      label: t('components->>SelectedSubjectReview.Subject Name'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { subject, subjectCode, status } = examData[dataIndex];
          return (
            <Box className={status === AppealEligibility.ELIGIBLE ? '' : classes.textDisabled}>
              <>{subject} </>
              <>{`(${subjectCode})`}</>
            </Box>
          );
        },
      },
    },
    {
      name: 'gradeAlphabetical',
      label: t('components->>SelectedSubjectReview.Grade Alphabetical'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { gradeAlphabetical, status } = examData[dataIndex];
          return (
            <Box className={status === AppealEligibility.ELIGIBLE ? '' : classes.textDisabled}>
              <>{gradeAlphabetical} </>
            </Box>
          );
        },
      },
    },
    {
      name: 'gradeNumerical',
      label: t('components->>SelectedSubjectReview.Grade Numerical'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { gradeNumerical, status } = examData[dataIndex];
          return (
            <Box className={status === AppealEligibility.ELIGIBLE ? '' : classes.textDisabled}>
              <>{gradeNumerical} </>
            </Box>
          );
        },
      },
    },
    {
      name: 'languageMedium',
      label: t('components->>SelectedSubjectReview.Language Medium'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { languageMedium, status } = examData[dataIndex];
          return (
            <Box className={status === AppealEligibility.ELIGIBLE ? '' : classes.textDisabled}>
              <>{languageMedium} </>
            </Box>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('components->>SelectedSubjectReview.Status'),
      options: {
        customBodyRender: (value: any) => {
          switch (true) {
            case value === AppealEligibility.SUBMITTED:
              return (
                <Pill
                  variant="info"
                  label={t('components->>SelectedSubjectReview.Appeal Submitted')}
                />
              );
            case value === AppealEligibility.PENDING_PAYMENT:
              return (
                <Pill variant="info" label={t('components->>SelectedSubjectReview.PendingPayment')} />
              );
            case value === AppealEligibility.NOT_ELIGIBLE:
              return (
                <Pill
                  variant="negative"
                  label={t('components->>SelectedSubjectReview.Not Eligible for Appeal')}
                />
              );
            case value === AppealEligibility.ELIGIBLE:
              return (
                <Pill
                  variant="positive"
                  label={t('components->>SelectedSubjectReview.Eligible for Appeal')}
                />
              );
            default:
              return null;
          }
        },
      },
    },
  ];

export interface SubjectSelectionTableProps {
  examData: AppealSubjectListingItem[];
  selectedSubjects: number[];
  onSelectSubject: (selectedSubject: number[]) => void;
}

const SubjectSelectionTable = ({
  examData,
  onSelectSubject,
  selectedSubjects,
}: SubjectSelectionTableProps) => {
  const { t } = useTranslation();
  const { state, dispatch, tOptions } = useDataTable(undefined, true);

  const isAllSubjectNotEligible = examData.every(
    (subject: AppealSubjectListingItem) => subject.status !== AppealEligibility.ELIGIBLE
  );

  const { classes } = useStyles({ isAllSubjectNotEligible });

  useEffect(() => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: examData,
        count: examData.length,
      },
    } as TableActionTypes);
  }, [dispatch, examData]);

  const datatableOptions: MUIDataTableOptions = {
    ...tOptions,
    selectableRows: 'multiple',
    rowsSelected: selectedSubjects,
    onRowSelectionChange: (currentRowsSelected: any, allRowsSelected: any, rowsSelected: any) => {
      if (tOptions.onRowSelectionChange) {
        // perform dataTable web-library onChange
        tOptions.onRowSelectionChange(currentRowsSelected, allRowsSelected, rowsSelected);
      }
      onSelectSubject(rowsSelected || []);
    },
    isRowSelectable: (dataIndex: number) => examData[dataIndex].status === AppealEligibility.ELIGIBLE,
    tableId: 'subjectSelection',
    setTableProps: () => ({ className: classes.dataTableSelectAllRowsCheckBox }),
  };

  return (
    <>
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid xs={12}>
          <Box id="action-table">
            <MUIDataTable
              state={state}
              dispatch={dispatch}
              columns={tableColumns(examData, t, classes)}
              data={examData}
              options={datatableOptions}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SubjectSelectionTable;
